import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { GraduationCap, ArrowLeftRight, Search } from "lucide-react";

const norm = (a) => [a.apellido_paterno, a.apellido_materno, a.nombre].filter(Boolean).join(" ");

export default function AlumnosLista() {
  const { user } = useAuth();
  const [planteles, setPlanteles] = useState([]);
  const [plantelId, setPlantelId] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [grupoFiltro, setGrupoFiltro] = useState("all");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [reasignar, setReasignar] = useState(null); // { alumno, grupoActual }
  const [nuevoGrupoId, setNuevoGrupoId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/planteles");
        setPlanteles(data);
        if (data.length) setPlantelId(data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!plantelId) return;
    setLoading(true);
    (async () => {
      try {
        const gs = (await api.get("/grupos", { params: { plantel_id: plantelId } })).data;
        setGrupos(gs);
        const rows = [];
        for (const g of gs) {
          const { data } = await api.get("/alumnos", { params: { grupo_id: g.id } });
          data.forEach((a) => rows.push({ ...a, __grupo: g }));
        }
        setItems(rows);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [plantelId]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items
      .filter((a) => grupoFiltro === "all" || a.grupo_id === grupoFiltro)
      .filter((a) => !term || norm(a).toLowerCase().includes(term) || (a.curp || "").toLowerCase().includes(term))
      .sort((a, b) => norm(a).localeCompare(norm(b), "es"));
  }, [items, grupoFiltro, q]);

  const gruposDestino = grupos.filter((g) => g.id !== reasignar?.alumno?.grupo_id);

  const confirmarReasignar = async () => {
    if (!reasignar?.alumno?.id || !nuevoGrupoId) return;
    setSaving(true);
    try {
      await api.put(`/alumnos/${reasignar.alumno.id}`, { grupo_id: nuevoGrupoId });
      const nuevo = grupos.find((g) => g.id === nuevoGrupoId);
      toast.success(`Alumno reasignado a ${nuevo.grado}° "${nuevo.letra}"`);
      setItems((prev) => prev.map((a) => a.id === reasignar.alumno.id ? { ...a, grupo_id: nuevoGrupoId, __grupo: nuevo } : a));
      setReasignar(null); setNuevoGrupoId("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Matrícula</div>
          <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Alumnos del plantel</h1>
          <p className="text-sm text-[#545454] mt-1">Vista rápida por grupo. Puede reasignar un alumno a otro grupo del mismo plantel.</p>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {["superadmin", "jefe_sector", "supervisor_zona"].includes(user?.role) && planteles.length > 1 && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Plantel</label>
              <Select value={plantelId} onValueChange={setPlantelId}>
                <SelectTrigger data-testid="al-plantel"><SelectValue /></SelectTrigger>
                <SelectContent>{planteles.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Grupo</label>
            <Select value={grupoFiltro} onValueChange={setGrupoFiltro}>
              <SelectTrigger data-testid="al-grupo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los grupos ({items.length})</SelectItem>
                {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{`${g.grado}° "${g.letra}"`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Buscar</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#9CA3AF]" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre o CURP…" className="pl-8" data-testid="al-search" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="py-2.5 px-3 text-left">Alumno</th>
                <th className="py-2.5 px-3 text-left">CURP</th>
                <th className="py-2.5 px-3 text-left">Grupo actual</th>
                <th className="py-2.5 px-3 text-right w-40">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={4} className="py-10 text-center text-[#9CA3AF]">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={4} className="py-10 text-center text-[#9CA3AF]">Sin alumnos con este filtro</td></tr>
              ) : filtered.map((a) => (
                <tr key={a.id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]">
                  <td className="py-2 px-3 uppercase">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#EAECEE] text-[#10312B] text-[10px] font-bold flex items-center justify-center">
                        <GraduationCap className="w-3.5 h-3.5" />
                      </div>
                      <span className="font-medium">{norm(a)}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs text-[#6B7280]">{a.curp || "—"}</td>
                  <td className="py-2 px-3">{a.__grupo ? `${a.__grupo.grado}° "${a.__grupo.letra}"` : "—"}</td>
                  <td className="py-2 px-3 text-right">
                    <Button
                      size="sm" variant="outline"
                      data-testid={`btn-reasignar-${a.id}`}
                      onClick={() => { setReasignar({ alumno: a, grupoActual: a.__grupo }); setNuevoGrupoId(""); }}
                      className="h-7 text-xs"
                    >
                      <ArrowLeftRight className="w-3 h-3 mr-1" /> Reasignar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!reasignar} onOpenChange={(o) => { if (!o) { setReasignar(null); setNuevoGrupoId(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reasignar a otro grupo</DialogTitle>
            <DialogDescription>
              El alumno mantendrá todos sus datos y calificaciones capturadas. Solo cambia el grupo.
            </DialogDescription>
          </DialogHeader>
          {reasignar && (
            <div className="space-y-3">
              <div className="p-3 bg-[#F9FAFB] rounded border border-[#F3F4F6]">
                <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">Alumno</div>
                <div className="text-sm font-semibold uppercase">{norm(reasignar.alumno)}</div>
                <div className="text-xs text-[#6B7280] mt-1">Grupo actual: <strong>{reasignar.grupoActual ? `${reasignar.grupoActual.grado}° "${reasignar.grupoActual.letra}"` : "—"}</strong></div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#6B7280] block mb-1">Nuevo grupo</label>
                <Select value={nuevoGrupoId} onValueChange={setNuevoGrupoId}>
                  <SelectTrigger data-testid="reasignar-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                  <SelectContent>
                    {gruposDestino.map((g) => <SelectItem key={g.id} value={g.id}>{`${g.grado}° "${g.letra}" · Ciclo ${g.ciclo}`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReasignar(null); setNuevoGrupoId(""); }}>Cancelar</Button>
            <Button
              disabled={!nuevoGrupoId || saving}
              onClick={confirmarReasignar}
              data-testid="btn-confirmar-reasignar"
              className="bg-[#235B4E] hover:bg-[#1D4A40] text-white"
            >
              {saving ? "Guardando…" : "Reasignar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
